import { Link } from "@tanstack/react-router";
import { Arrow, Check } from "~/components/icons";
import { Reveal } from "~/components/reveal";

const proofPoints = ["From $2.99/month", "Fixed monthly billing"] as const;
const popularApplications = [
  "WordPress",
  "Laravel",
  "Django",
  "Next.js",
  "Nuxt.js",
] as const;

export function HomeHero() {
  return (
    <div className="mx-auto grid min-h-[calc(100svh-4.5rem)] max-w-7xl items-center gap-14 px-5 pb-16 pt-16 lg:grid-cols-[1.15fr_.85fr] lg:gap-12 lg:px-12 lg:pb-20 lg:pt-24">
      <Reveal className="max-w-[680px] self-center">
        <h1 className="display max-w-[680px] text-5xl font-bold leading-[.96] tracking-[-0.065em] sm:text-6xl lg:text-[4.1rem] xl:text-[4.5rem]">
          Host more than a <span className="hero-highlight">website </span>{" "}
          <span>without managing a server</span>
        </h1>
        <p className="mt-8 max-w-xl text-[1.15rem] leading-8 text-ink/65 lg:text-[1.125rem]">
          Host websites, database and applications from one straightforward
          account. Email, SSL, DNS, backups, Redis, and Cron Jobs are included
          with all plans.
        </p>
        <div
          className="mt-10 flex flex-wrap items-center gap-2"
          aria-label="Popular supported applications"
        >
          {popularApplications.map((application) => (
            <span
              key={application}
              className="rounded-full border border-ink/20 bg-white px-3 py-1.5 text-xs font-bold text-ink/75 shadow-sm"
            >
              {application}
            </span>
          ))}
          <Link
            to="/knowledge-base/$"
            params={{ _splat: "application-hosting" }}
            className="px-2 py-1.5 text-xs font-bold text-moss transition hover:text-ink"
          >
            +20 more →
          </Link>
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/products/web-hosting/shared-hosting"
            className="button-primary rounded-full px-6 py-3.5 text-sm font-bold shadow-sm transition motion-safe:hover:-translate-y-0.5 focus-visible:outline-3 focus-visible:outline-leaf focus-visible:outline-offset-3"
          >
            See hosting plans
          </Link>
          <button
            type="button"
            onClick={() =>
              window.dispatchEvent(new CustomEvent("hostright:open-ai"))
            }
            className="group flex items-center gap-2 rounded-full border border-ink/15 bg-white/45 px-6 py-3.5 text-sm font-bold transition hover:border-moss/35 hover:bg-white focus-visible:outline-3 focus-visible:outline-leaf focus-visible:outline-offset-3"
          >
            Ask before you buy <Arrow />
          </button>
        </div>
        <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3 text-[0.9375rem] font-bold text-ink/80">
          {proofPoints.map((detail) => (
            <span key={detail} className="flex items-center gap-2">
              <Check />
              {detail}
            </span>
          ))}
        </div>
      </Reveal>
      <div className="relative hidden w-full max-w-[500px] lg:block lg:justify-self-end">
        <div className="absolute -right-5 top-4 h-52 w-52 rounded-full bg-leaf/55 blur-3xl" />
        <img
          className="relative aspect-[4/5] w-full rounded-[2rem] border border-ink/10 object-cover object-[center_42%] shadow-[0_24px_60px_rgba(13,43,34,.18),8px_8px_0_#1d654f]"
          src="/images/landing-human.webp"
          alt="A smiling business owner in a bright workspace"
        />
        <aside className="absolute bottom-6 left-6 w-64 rounded-2xl border border-ink/10 bg-white p-4 shadow-[0_14px_32px_rgba(13,43,34,.15)]">
          <p className="text-[0.85rem] leading-6">
            We went from raw EC2 setup headaches to zero stress Django
            deployment.
          </p>
          <p className="mt-3 text-[0.8125rem] leading-5 text-ink/55">
            <span className="text-sm font-bold text-ink">Iker Crivello</span>
            <br />
            Senior Django Engineer
          </p>
        </aside>
      </div>
    </div>
  );
}
